import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number | null} rAF handle for the open-state offset tracker */
  #offsetRaf = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onLinkClick);
    // Capture phase: the <details> `toggle` event does not bubble.
    this.addEventListener('toggle', this.#onAccordionToggle, { capture: true });
    window.addEventListener('resize', this.#onResize);

    // Stop the native <details> from toggling `open` on the main summary; open()
    // / close() own that attribute. The native toggle raced with our JS toggle
    // and left the icon (menu-open class) out of sync with the drawer content
    // (which needs the `open` attribute to be visible in modern browsers).
    this.#mainSummary = this.refs?.details?.querySelector(':scope > summary') ?? null;
    this.#mainSummary?.addEventListener('click', this.#preventNativeToggle);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onLinkClick);
    this.removeEventListener('toggle', this.#onAccordionToggle, { capture: true });
    window.removeEventListener('resize', this.#onResize);
    this.#mainSummary?.removeEventListener('click', this.#preventNativeToggle);
    this.#stopTrackingDrawerTopOffset();
  }

  /** @type {HTMLElement | null} the main drawer summary (hamburger) */
  #mainSummary = null;

  /** Prevent the native <details> toggle so JS owns the `open` attribute. */
  #preventNativeToggle = (event) => {
    event.preventDefault();
  };

  /**
   * On resize (e.g. orientation change / URL-bar collapse) the header height can
   * change; re-track the offset while the drawer is open.
   */
  #onResize = () => {
    if (this.isOpen) this.#trackDrawerTopOffset();
  };

  /**
   * Keep only one top-level group accordion open at a time in the drawer.
   * When one opens, close the other top-level accordions (nested accordions are
   * left alone). `toggle` doesn't bubble, so this runs on the capture phase.
   * @param {Event} event
   */
  #onAccordionToggle = (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

    const topList = this.querySelector('.menu-drawer__menu.has-submenu');
    if (!topList) return;

    // Top-level group accordions are `li > accordion-custom > details` directly
    // under the main drawer menu list. The top-level <li> carries modifier
    // classes (--deep/--divider) rather than the base class, so match `> li`.
    const topDetails = topList.querySelectorAll(':scope > li > accordion-custom > details');
    if (!Array.prototype.includes.call(topDetails, opened)) return;

    topDetails.forEach((details) => {
      if (details !== opened && details.open) details.open = false;
    });
  };

  /**
   * Close the whole drawer when a navigational link is tapped. Accordion
   * toggles are <summary> (not <a>), so expanding a section is unaffected;
   * only leaf links close the drawer. This matters for same-page anchor links
   * (e.g. the "How it works" section jumps) where there is no page reload to
   * close the drawer on its own — otherwise the open menu hides the section
   * the user was taken to.
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    if (this.isOpen) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   *
   * Tracked via the `menu-open` class, which open()/close() set and clear
   * synchronously — not the `open` attribute, which lingers through the close
   * animation and is toggled by the native <details>, both of which race with
   * our JS toggle and desynced the icon from the drawer.
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // JS owns the `open` attribute now (native toggle is prevented on the main
    // summary). Set it synchronously so the details content is visible; the
    // menu-open class (added next frame) drives the slide-in transition.
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');

    // Freeze the sticky header up front (synchronously, before any layout read)
    // so it can't flip to the hidden `idle` state between now and the open frame.
    this.#lockHeader();
    this.#updateDrawerTopOffset();

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      // Keep the header frozen and track its bottom edge every frame until it
      // holds steady so the drawer always docks exactly beneath it.
      this.#lockHeader();
      this.#trackDrawerTopOffset();

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Only stop offset tracking when the whole drawer is closing — a submenu
    // "back" keeps the drawer (and the freeze) in place.
    if (details === this.refs.details) {
      this.#stopTrackingDrawerTopOffset();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          // Release the header freeze only now — after the close animation and
          // reset (scroll-lock lifted). Unlocking earlier reverts the header
          // from fixed to sticky while scroll-lock is still on, which drops it
          // for a frame and makes it blink.
          this.#unlockHeader();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Measure the visible bottom edge of the header cluster (announcement bar +
   * header) and expose it as --drawer-top-offset so the mobile drawer can sit
   * directly below it without overlapping. Robust across the announcement bar,
   * transparent headers, and sticky state (unlike --header-group-height, which
   * excludes the header when it is transparent / out of flow).
   */
  #updateDrawerTopOffset = () => {
    const header = this.closest('header-component') ?? document.querySelector('header-component');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    // When the page is already scrolled the sticky header can be mid-transition
    // or (with a non-sticky top row) have its top edge above the viewport, which
    // made the old `max(0, bottom)` collapse the offset to 0 and let the drawer
    // slide under the visible header. Use the header's on-screen bottom when it
    // is showing, otherwise fall back to its height so the drawer still starts
    // just below where the header sits.
    const bottom = rect.bottom > 0 ? Math.round(rect.bottom) : Math.round(rect.height);
    this.style.setProperty('--drawer-top-offset', `${bottom}px`);
    return bottom;
  };

  /**
   * Keep --drawer-top-offset pinned to the header's bottom edge while the drawer
   * is open. The sticky header can animate/transition for a few hundred ms after
   * open (especially when the page was already scrolled), so we sample each frame
   * and stop once the value has held steady, or when the drawer closes.
   */
  #trackDrawerTopOffset = () => {
    if (this.#offsetRaf != null) cancelAnimationFrame(this.#offsetRaf);
    let last = this.#updateDrawerTopOffset();
    let stable = 0;
    const tick = () => {
      if (!this.isOpen) {
        this.#offsetRaf = null;
        return;
      }
      const next = this.#updateDrawerTopOffset();
      if (next === last) {
        stable += 1;
      } else {
        stable = 0;
        last = next;
      }
      // ~20 steady frames (~1/3s) after it last changed, then stop sampling.
      this.#offsetRaf = stable < 20 ? requestAnimationFrame(tick) : null;
    };
    this.#offsetRaf = requestAnimationFrame(tick);
  };

  #stopTrackingDrawerTopOffset = () => {
    if (this.#offsetRaf != null) {
      cancelAnimationFrame(this.#offsetRaf);
      this.#offsetRaf = null;
    }
  };

  /** @returns {HTMLElement | null} the header custom element */
  #getHeader() {
    return this.closest('header-component') ?? document.querySelector('header-component');
  }

  /** @type {string | null} sticky state to restore when the drawer closes */
  #prevStickyState = null;

  /**
   * Freeze the sticky header while the drawer is open: mark it locked (header.js
   * then ignores scroll/observer state changes) and force the solid "active"
   * appearance. On a transparent header the non-active state collapses the
   * background underlay to 0 height, so the page would show through the header
   * (and the transparent-colour logo/icons disappear) behind the open menu.
   */
  #lockHeader = () => {
    const header = this.#getHeader();
    if (!header) return;
    if (!header.hasAttribute('data-drawer-locked')) {
      this.#prevStickyState = header.dataset.stickyState || '';
    }
    header.setAttribute('data-drawer-locked', '');
    header.dataset.stickyState = 'active';
  };

  #unlockHeader = () => {
    const header = this.#getHeader();
    if (!header) return;
    header.removeAttribute('data-drawer-locked');
    // Restore the pre-open state; the next scroll re-derives it anyway.
    if (this.#prevStickyState !== null) {
      if (this.#prevStickyState) {
        header.dataset.stickyState = this.#prevStickyState;
      } else {
        delete header.dataset.stickyState;
      }
      this.#prevStickyState = null;
    }
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
